import type {
  EstadoWebchat,
  EtiquetaDisponible,
  WebchatAtajo,
  WebchatBotPatch,
  WebchatCapacidades,
  WebchatCatalogos,
  WebchatChecklist,
  WebchatDetalle,
  WebchatFichaPatch,
  WebchatListItem,
  WebchatOperacion,
  WebchatRecurso,
  WebchatVersion,
  ValoresTema,
} from "@bo/shared";
import { api } from "@/lib/api.js";

/**
 * Las llamadas del módulo Webchats (prefijo /webchats del API). Las claves de
 * React Query viven acá para que cada pantalla invalide lo mismo.
 */

export const claves = {
  raiz: ["webchats"] as const,
  listado: (filtros: FiltrosListado) => ["webchats", "listado", filtros] as const,
  capacidades: ["webchats", "capacidades"] as const,
  catalogos: (idcompany: number | null) => ["webchats", "catalogos", idcompany] as const,
  detalle: (idcanal: number) => ["webchats", "detalle", idcanal] as const,
  versiones: (idcanal: number) => ["webchats", "versiones", idcanal] as const,
  version: (idcanal: number, version: number) => ["webchats", "version", idcanal, version] as const,
  operacion: (id: number) => ["webchats", "operacion", id] as const,
  auditoria: (idcanal: number) => ["webchats", "auditoria", idcanal] as const,
};

export interface FiltrosListado {
  q?: string;
  idcompany?: number | null;
  estado?: EstadoWebchat | null;
  page: number;
  pageSize: number;
}

export interface PaginaListado {
  items: WebchatListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface VersionDetalle extends WebchatVersion {
  snapshot: {
    tema: { estado: number; valores: ValoresTema } | null;
    atajos: { ORDEN: number; ETIQUETA: string; ACCION: string }[];
    recursos: { ranura: string; mime: string; sha256: string }[];
  };
}

export interface ResultadoSubida {
  recurso: WebchatRecurso;
  /** data: de la miniatura recién validada (las vistas siguientes usan la URL pública). */
  vistaPrevia: string;
  version: number;
  avisos: string[];
}

export interface EntradaAuditoria {
  id: number;
  at: string;
  actorEmail: string | null;
  action: string;
  resourceId: string | null;
  result: string;
  reason: string | null;
}

function consulta(params: Record<string, string | number | null | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== null && v !== undefined && v !== "") u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

const base = "/webchats";

export const webchatsApi = {
  listar: (f: FiltrosListado) =>
    api.get<PaginaListado>(
      `${base}${consulta({ q: f.q?.trim(), idcompany: f.idcompany, estado: f.estado, page: f.page, pageSize: f.pageSize })}`,
    ),
  capacidades: () => api.get<WebchatCapacidades>(`${base}/capacidades`),
  catalogos: (idcompany: number | null) => api.get<WebchatCatalogos>(`${base}/catalogos${consulta({ idcompany })}`),
  etiquetaDisponible: (etiqueta: string) =>
    api.get<EtiquetaDisponible>(`${base}/etiqueta-disponible${consulta({ etiqueta })}`),
  detalle: (idcanal: number, fresco = false) =>
    api.get<WebchatDetalle>(`${base}/${idcanal}${fresco ? "?fresco=1" : ""}`),
  versiones: (idcanal: number) => api.get<{ items: WebchatVersion[] }>(`${base}/${idcanal}/versiones`),
  version: (idcanal: number, version: number) => api.get<VersionDetalle>(`${base}/${idcanal}/versiones/${version}`),
  operacion: (id: number) => api.get<WebchatOperacion>(`${base}/operaciones/${id}`),
  auditoria: (idcanal: number) =>
    api.get<{ items: EntradaAuditoria[] }>(`/audit${consulta({ resourceType: "webchat", resourceId: idcanal, pageSize: 50 })}`),

  alta: (pedido: unknown) => api.post<{ operacion: WebchatOperacion }>(base, pedido),
  reanudar: (id: number) => api.post<{ operacion: WebchatOperacion }>(`${base}/operaciones/${id}/reanudar`, {}),
  descartar: (id: number, motivo?: string) =>
    api.post<{ operacion: WebchatOperacion }>(`${base}/operaciones/${id}/descartar`, motivo ? { motivo } : {}),
  verificar: (idcanal: number) => api.post<WebchatChecklist>(`${base}/${idcanal}/verificar`, {}),

  ficha: (idcanal: number, cambios: WebchatFichaPatch) => api.patch<{ version: string }>(`${base}/${idcanal}/ficha`, cambios),
  bot: (idcanal: number, cambios: Partial<WebchatBotPatch>) =>
    api.patch<{ idbot: number; namebot: string | null; activebot: boolean; verificacion: string }>(`${base}/${idcanal}/bot`, cambios),
  tema: (idcanal: number, version: string | null, valores: ValoresTema) =>
    api.put<{ version: number }>(`${base}/${idcanal}/tema`, { version, valores }),
  atajos: (idcanal: number, version: string | null, atajos: WebchatAtajo[]) =>
    api.put<{ version: number }>(`${base}/${idcanal}/atajos`, { version, atajos }),
  subirRecurso: (idcanal: number, ranura: string, archivo: Blob) =>
    api.putBinary<ResultadoSubida>(`${base}/${idcanal}/recursos/${ranura}`, archivo),
  borrarRecurso: (idcanal: number, ranura: string) =>
    api.delete<{ version: number | null; avisos: string[] }>(`${base}/${idcanal}/recursos/${ranura}`),
  revertir: (idcanal: number, version: number, motivo: string) =>
    api.post<{ version: number }>(`${base}/${idcanal}/versiones/${version}/revertir`, { motivo }),

  suspender: (idcanal: number, motivo: string) =>
    api.post<{ estado: number; cambio: boolean }>(`${base}/${idcanal}/suspender`, { motivo }),
  reactivar: (idcanal: number, motivo: string) =>
    api.post<{ estado: number; cambio: boolean }>(`${base}/${idcanal}/reactivar`, { motivo }),
  baja: (idcanal: number, pedido: { motivo: string; confirmarHost: string; idempotencyKey: string }) =>
    api.post<{ operacion: WebchatOperacion }>(`${base}/${idcanal}/baja`, pedido),
  sincronizarRegla: (idcanal: number) =>
    api.post<{ cambio: boolean; avisos: string[] }>(`${base}/${idcanal}/regla/sincronizar`, {}),
};
